import * as moment from 'moment';
import { DashRegistrationStep } from "@nkr/enums/index";
import { IDashWorkFlowStatusRequest } from "@nkr/models/dash-workflow-status-request";
import { IDashRemoteStatus } from "@nkr/models/donor-status-by-center";
import { IDonorRegistration, IDonorStatus, IWorkflowStatus } from "@nkr/models/index";
import React, { FormEvent, useEffect, useState, useRef } from "react";
import { Button, Col, ControlLabel, FormControl, FormGroup, Row } from "react-bootstrap";
import { toast } from 'react-toastify';
import RuleOutDonorModal from './rule-out-donor-modal';
import { TypeParser } from '@nkr/utils/form';
import extractGambitData from '@nkr/utils/extract-gambit-data';
import { GambitType } from '@nkr/models/gambit-type';
import { IGambitData } from '@nkr/models/gambit-data';
import { ICoordinator } from '../coordinator';
import { bannerStyle, cardBodyStyle, cardFooterStyle, cardHeaderStyle, cardStyle, cardTitleRowStyle, cardTitleTextStyle, dangerBannerStyle, fieldLabelStyle, fieldSurfaceStyle, inlineFieldInputWrapStyle, inlineFieldLabelStyle, inlineFieldRowStyle, pendingChangesStyle, primaryBtnStyle, sectionHelperStyle } from '../styles';
import { isPreOpScheduledWithFutureDate, isWorkupScheduledWithFutureDate, PRE_OP_SCHEDULED_REMOTE_STATUS_ID, WORKUP_SCHEDULED_REMOTE_STATUS_ID } from './advanced-reimbursement-workflow';

const reviewCommentsMax = 2000;

interface IProps {
    data: IDonorRegistration,
    statusList: (IDonorStatus | IWorkflowStatus)[],
    remoteStatusList: IDashRemoteStatus[],
    coList?: ICoordinator[],
    useWideFormat: boolean,
    isReadOnly?: boolean,
    ruleOutReasons?: Array<{ id: number; description: string }>,
    onDataChange: (field: string, value: any) => void,
    onUpdate: (request: IDashWorkFlowStatusRequest) => void,
    onSaveCenterCard?: () => void | Promise<void>,
    onSaveNetworkCard?: () => void | Promise<void>,
    onRuleOut?: (donorId: number, reasonId: number, allowReRegistration: boolean, useNkrAssistance: boolean, coordinatorName?: string, phoneNumber?: string, medicalReasonCategory?: number, medicalReasonNotes?: string) => void
}

const DashStatusForm = (props: IProps) => {
    const workupScheduledStatusId = WORKUP_SCHEDULED_REMOTE_STATUS_ID;
    const preOpScheduledStatusId = PRE_OP_SCHEDULED_REMOTE_STATUS_ID;
    const [showRuleOutModal, setShowRuleOutModal] = useState(false);
    const [pendingStatusId, setPendingStatusId] = useState<number | null>(null);
    const [preSelectedReasonId, setPreSelectedReasonId] = useState<number | null>(null);
    const [showOnHoldStatusField, setShowOnHoldStatusField] = useState<boolean>(props.data.remoteStatus == 1);
    const [onHoldStatusReason, setOnHoldStatusReason] = useState<IGambitData | null>(null);
    const [preventNetworkSave, setPreventNetworkSave] = useState<boolean>(false);
    const [localComments, setLocalComments] = useState<string>(props.data.reviewComments || '');
    const [focusedDateField, setFocusedDateField] = useState<'centerNextFollowUpDate' | 'remoteStatusDate' | null>(null);
    const [dateDraft, setDateDraft] = useState<string>('');
    const centerSnapshotRef = useRef<string>('');
    const networkSnapshotRef = useRef<string>('');

    const createCenterSnapshot = (commentsOverride?: string) => JSON.stringify({
        k: props.data.coordinatorId ?? 0,
        w: props.data.centerWorkflowId,
        f: props.data.centerNextFollowUpDate,
        c: commentsOverride !== undefined ? commentsOverride : props.data.reviewComments
    });

    const createNetworkSnapshot = () => JSON.stringify({
        r: props.data.remoteStatus,
        rd: props.data.remoteStatusDate,
        o: props.data.onHoldStatusReason
    });

    useEffect(() => {
        if (props.data.dashGambitData) {
            const value = extractGambitData(props.data.dashGambitData, GambitType.DonotIntakeOnHoldReason_DonorRemoteStatus);
            setOnHoldStatusReason(value);
            if (value && value.collectedData && !props.data.onHoldStatusReason) {
                props.data.onHoldStatusReason = value.collectedData;
            }
        }
        setLocalComments(props.data.reviewComments || '');
        centerSnapshotRef.current = createCenterSnapshot();
        networkSnapshotRef.current = createNetworkSnapshot();
    }, [props.data.id]);

    const handleFieldChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;

        if (['centerWorkflowId', 'remoteStatus', 'coordinatorId'].includes(name)) {
            let numVal = Number(e.currentTarget.value);

            props.onDataChange(name, numVal);

            if (name === 'remoteStatus' && numVal === 1) {
                setShowOnHoldStatusField(true);
                setPreventNetworkSave(onHoldStatusReason?.collectedData === '');
            } else if (name === 'remoteStatus') {
                props.data.onHoldStatusReason = '';
                setShowOnHoldStatusField(false);
                setPreventNetworkSave(false);

                props.onDataChange(name, e.currentTarget.value);
            }

            if (name === 'remoteStatus' && focusedDateField === 'remoteStatusDate') {
                setFocusedDateField(null);
                setDateDraft('');
            }

            if (name === "centerWorkflowId" && numVal > 0 && props.ruleOutReasons && props.onRuleOut) {
                const selectedStatus = props.statusList.find(s => s.id === numVal);

                const isRuleOutStatus = selectedStatus && selectedStatus.isRuledOut === true;

                const isAlreadyRuledOut = props.data.currentRegistrationStep >= DashRegistrationStep.AutoRuledOut;

                if (isRuleOutStatus && !isAlreadyRuledOut) {
                    setPreSelectedReasonId(selectedStatus.associatedDisposition);
                    setPendingStatusId(numVal);
                    setShowRuleOutModal(true);
                }
            }

        } else if (name === 'onHoldStatusReason') {
            props.data.onHoldStatusReason = value;

            setOnHoldStatusReason(prev => {
                return { ...prev, collectedData: value };
            });

            setPreventNetworkSave(value === '');

            props.onDataChange(name, value);
        } else if (name === 'reviewComments') {
            if (value.length <= reviewCommentsMax) {
                setLocalComments(value);
                props.onDataChange('reviewComments', value);
            }
        } else {
            props.onDataChange(name, value);
        }
    }

    const handleRuleOutConfirm = (reasonId: number, allowReRegistration: boolean, useNkrAssistance: boolean, coordinatorName?: string, phoneNumber?: string, medicalReasonCategory?: number, medicalReasonNotes?: string) => {
        if (pendingStatusId !== null) {
            props.onRuleOut(props.data.id, reasonId, allowReRegistration, useNkrAssistance, coordinatorName, phoneNumber, medicalReasonCategory, medicalReasonNotes);

            setShowRuleOutModal(false);
            setPendingStatusId(null);
            setPreSelectedReasonId(null);
        }
    };

    const handleRuleOutCancel = () => {
        setShowRuleOutModal(false);
        setPendingStatusId(null);
        setPreSelectedReasonId(null);

        props.onDataChange("centerWorkflowId", props.data.centerWorkflowId || -1);
    };

    const isValidDate = (date: string) => date && moment(date, 'MM/DD/YYYY').isValid();

    const formatStoredDateForInput = (stored: string | null | undefined) => {
        if (!stored) {
            return '';
        }
        const parsed = moment(stored, ['MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD'], true);
        if (parsed.isValid()) {
            return parsed.format('YYYY-MM-DD');
        }
        const loose = moment(stored);
        return loose.isValid() ? loose.format('YYYY-MM-DD') : '';
    };

    const getDateInputValue = (field: 'centerNextFollowUpDate' | 'remoteStatusDate', stored: string | null | undefined) =>
        focusedDateField === field ? dateDraft : formatStoredDateForInput(stored);

    const handleDateFocus = (field: 'centerNextFollowUpDate' | 'remoteStatusDate', stored: string | null | undefined) => {
        setFocusedDateField(field);
        setDateDraft(formatStoredDateForInput(stored));
    };

    const handleDateChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        setDateDraft(value);

        if (!value) {
            props.onDataChange(name, '');
            return;
        }

        const date = moment(value, 'YYYY-MM-DD', true);
        if (date.isValid()) {
            // Commit complete dates so Save works without blur; do not snap past→today here (Chrome year typing). 
            props.onDataChange(name, date.format('MM/DD/YYYY'));
        }
    };

    const handleDateBlur = (field: 'centerNextFollowUpDate' | 'remoteStatusDate') => {
        const raw = dateDraft;
        setFocusedDateField(null);
        setDateDraft('');

        if (!raw) {
            props.onDataChange(field, '');
            return;
        }

        const date = moment(raw, 'YYYY-MM-DD', true);
        if (!date.isValid()) {
            return;
        }

        if (field === 'centerNextFollowUpDate' && date.isBefore(moment(), 'day')) {
            props.onDataChange(field, moment().format('MM/DD/YYYY'));
            return;
        }

        props.onDataChange(field, date.format('MM/DD/YYYY'));
    };

    const isWorkupScheduled = Number(props.data.remoteStatus) === WORKUP_SCHEDULED_REMOTE_STATUS_ID;
    const isPreOpScheduled = Number(props.data.remoteStatus) === PRE_OP_SCHEDULED_REMOTE_STATUS_ID;
    const isEditingRemoteStatusDate = focusedDateField === 'remoteStatusDate';
    const isWorkupScheduledDateFuture = !isWorkupScheduled || isWorkupScheduledWithFutureDate(props.data);
    const isPreOpScheduledDateFuture = !isPreOpScheduled || isPreOpScheduledWithFutureDate(props.data);
    const showWorkupFutureDateError = !isEditingRemoteStatusDate && !isWorkupScheduledDateFuture && isWorkupScheduled;
    const showPreOpFutureDateError = !isEditingRemoteStatusDate && !isPreOpScheduledDateFuture && isPreOpScheduled;

    const createStatusUpdateRequest = (): IDashWorkFlowStatusRequest => {
        return {
            statusId: props.data.centerWorkflowId || -1,
            centerNextFollowUpDate: props.data.centerNextFollowUpDate || null,
            reviewComments: localComments || null,
            remoteStatus: TypeParser.getIntOrDefault(props.data.remoteStatus),
            onHoldStatusReason: props.data.onHoldStatusReason || null,
            remoteStatusDate: props.data.remoteStatusDate || null,
            ruleOutReasonId: null,
            allowReRegistration: null,
            useNkrAssistance: null,
            coordinatorName: null,
            phoneNumber: null,
            medicalReasonCategory: null,
            medicalReasonNotes: null,
            centerId: props.data.centerId,
            registrationId: props.data.id
        };
    };

    const selectedStatus = props.statusList.find(item => item.id === Number(props.data.centerWorkflowId));
    const selectedRemoteStatus = props.remoteStatusList.find(item => item.id === Number(props.data.remoteStatus));
    const isNetworkStatusRequired = props.data.recipientCenterId > 0 && props.data.recipientCenterId !== props.data.centerId;
    const requiresFollowUp = (selectedStatus as IWorkflowStatus)?.addFollowUp || (selectedStatus as IDonorStatus)?.requiresFollowUp || false;
    const isCenterWorkflowValid = selectedStatus?.id > 0 && (!requiresFollowUp || isValidDate(props.data.centerNextFollowUpDate));
    const isNetworkStatusValid = !isNetworkStatusRequired || (selectedRemoteStatus != null && selectedRemoteStatus.id >= 0);
    const isNetworkStatusDateValid = !selectedRemoteStatus?.requiresDate || isValidDate(props.data.remoteStatusDate);
    const isNetworkCardValid = isNetworkStatusValid && isNetworkStatusDateValid && isWorkupScheduledDateFuture && isPreOpScheduledDateFuture && !preventNetworkSave;
    const isCenterCardValid = isCenterWorkflowValid;
    const commentsLen = localComments.length;

    let isCenterDirty = false;

    if (!centerSnapshotRef.current) {
        centerSnapshotRef.current = createCenterSnapshot();
    }

    try {
        const s = JSON.parse(centerSnapshotRef.current || '{}');

        isCenterDirty =
            Number(s.k ?? 0) !== Number(props.data.coordinatorId ?? 0)
            || Number(s.w ?? -1) !== Number(props.data.centerWorkflowId ?? -1)
            || String(s.f || '') !== String(props.data.centerNextFollowUpDate || '')
            || String(s.c || '') !== String(localComments);
    } catch { /* noop */ }

    const discardCenter = () => {
        try {
            const s = JSON.parse(centerSnapshotRef.current);
            props.onDataChange('coordinatorId', Number(s.k ?? 0));
            props.onDataChange('centerWorkflowId', s.w ?? -1);
            props.onDataChange('centerNextFollowUpDate', s.f || '');
            setLocalComments(s.c || '');
            if (focusedDateField === 'centerNextFollowUpDate') {
                setFocusedDateField(null);
                setDateDraft('');
            }
        } catch { /* noop */ }
    };

    const discardNetwork = () => {
        try {
            const s = JSON.parse(networkSnapshotRef.current);
            props.onDataChange('remoteStatus', s.r ?? -1);
            props.onDataChange('remoteStatusDate', s.rd || '');
            props.onDataChange('onHoldStatusReason', s.o || '');
            const rs = s.r;
            setShowOnHoldStatusField(rs == 1);
            setPreventNetworkSave(rs == 1 && !s.o);
            if (focusedDateField === 'remoteStatusDate') {
                setFocusedDateField(null);
                setDateDraft('');
            }
        } catch { /* noop */ }
    };

    const saveCenter = async () => {
        if (!isCenterDirty) {
            return;
        }

        if (!isCenterCardValid) {
            toast.error('Please correct workflow fields before saving.');
            return;
        }

        try {
            if (props.onSaveCenterCard) {
                await props.onSaveCenterCard();

                props.onDataChange('reviewComments', localComments);

                centerSnapshotRef.current = createCenterSnapshot(localComments);
            } else {
                if (!isNetworkCardValid) {
                    toast.error('Please correct network status before saving.');
                    return;
                }

                props.onUpdate(createStatusUpdateRequest());
                props.onDataChange('reviewComments', localComments);

                centerSnapshotRef.current = createCenterSnapshot(localComments);
            }
        } catch (err) {
            toast.error('Save failed.');
        }
    };

    const saveNetwork = async () => {
        if (!isNetworkCardValid) {
            toast.error('Please correct network status before saving.');
            return;
        }

        try {
            if (props.onSaveNetworkCard) {
                await props.onSaveNetworkCard();

                networkSnapshotRef.current = createNetworkSnapshot();
            } else {
                if (!isCenterCardValid) {
                    toast.error('Please correct workflow before saving.');
                    return;
                }

                props.onUpdate(createStatusUpdateRequest());

                networkSnapshotRef.current = createNetworkSnapshot();
            }
        } catch (err) {
            toast.error('Save failed.');
        }
    };

    return (
        <>
            <div style={cardStyle}>
                <div style={cardHeaderStyle}>
                    <div style={cardTitleRowStyle}>
                        {/* <span style={cardNumStyle}>1</span> */}
                        <span style={cardTitleTextStyle}>AT YOUR CENTER & COMMENTS</span>
                    </div>
                    {isCenterDirty && <span style={pendingChangesStyle}>Pending Unsaved Changes</span>}
                </div>
                <div style={cardBodyStyle}>
                    <p style={sectionHelperStyle}>Your center's steps for this registration.</p>

                    <Row>
                        <Col xs={12} md={4}>
                            <FormGroup style={{ marginBottom: 20 }}>
                                <ControlLabel htmlFor='coordinatorId' style={fieldLabelStyle}>COORDINATOR</ControlLabel>
                                <FormControl
                                    id='coordinatorId'
                                    name='coordinatorId'
                                    componentClass='select'
                                    className='form-control'
                                    style={fieldSurfaceStyle}
                                    value={props.data.coordinatorId ?? 0}
                                    disabled={props.isReadOnly}
                                    onChange={handleFieldChange}
                                >
                                    <option value={0}>- Not Specified -</option>
                                    {(props.coList || [])
                                        .filter(x => x.coordinatorType == props.data.donationType || x.coordinatorType == 2)
                                        .map(coordinator =>
                                            <option key={'coordinator-' + coordinator.contactId} value={coordinator.contactId}>{coordinator.fullName}</option>
                                        )}
                                </FormControl>
                            </FormGroup>
                        </Col>

                        {requiresFollowUp ? (
                            <>
                                <Col xs={12} md={4}>
                                    <FormGroup validationState={isCenterWorkflowValid ? null : 'error'} style={{ marginBottom: 20 }}>
                                        <ControlLabel htmlFor='centerWorkflowId' style={fieldLabelStyle}>WORKFLOW STATUS *</ControlLabel>
                                        <FormControl
                                            id='centerWorkflowId'
                                            name='centerWorkflowId'
                                            componentClass='select'
                                            className='form-control'
                                            style={fieldSurfaceStyle}
                                            value={props.data.centerWorkflowId ?? -1}
                                            disabled={props.isReadOnly}
                                            onChange={handleFieldChange}
                                        >
                                            <option value='-1'>Not Set</option>
                                            {props.statusList.map((item, index) =>
                                                <option key={`stat-${index}`} value={item.id}>{item.description}</option>
                                            )}
                                        </FormControl>
                                        <div className='help-block' style={{ marginBottom: 0 }}>
                                            <a href={`/center/${props.data.centerId}/profile/workflow-config`} target='_blank' rel='noreferrer'>Manage Status List</a>
                                        </div>
                                    </FormGroup>
                                </Col>
                                <Col xs={12} md={4}>
                                    <FormGroup validationState={props.data.centerNextFollowUpDate ? null : 'error'} style={{ marginBottom: 20 }}>
                                        <ControlLabel style={fieldLabelStyle}>NEXT FOLLOW-UP DATE</ControlLabel>
                                        <FormControl
                                            type='date'
                                            id='centerNextFollowUpDate'
                                            name='centerNextFollowUpDate'
                                            style={fieldSurfaceStyle}
                                            value={getDateInputValue('centerNextFollowUpDate', props.data.centerNextFollowUpDate)}
                                            disabled={props.isReadOnly}
                                            onFocus={() => handleDateFocus('centerNextFollowUpDate', props.data.centerNextFollowUpDate)}
                                            onChange={handleDateChange}
                                            onBlur={() => handleDateBlur('centerNextFollowUpDate')}
                                        />
                                    </FormGroup>
                                </Col>
                            </>
                        ) : (
                            <Col xs={12} md={8}>
                                <FormGroup validationState={isCenterWorkflowValid ? null : 'error'} style={{ marginBottom: 20 }}>
                                    <div style={inlineFieldRowStyle}>
                                        <ControlLabel htmlFor='centerWorkflowId' style={inlineFieldLabelStyle}>WORKFLOW STATUS *</ControlLabel>
                                        <div style={inlineFieldInputWrapStyle}>
                                            <FormControl
                                                id='centerWorkflowId'
                                                name='centerWorkflowId'
                                                componentClass='select'
                                                className='form-control'
                                                style={fieldSurfaceStyle}
                                                value={props.data.centerWorkflowId ?? -1}
                                                disabled={props.isReadOnly}
                                                onChange={handleFieldChange}
                                            >
                                                <option value='-1'>Not Set</option>
                                                {props.statusList.map((item, index) =>
                                                    <option key={`stat-${index}`} value={item.id}>{item.description}</option>
                                                )}
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className='help-block' style={{ marginBottom: 0 }}>
                                        <a href={`/center/${props.data.centerId}/profile/workflow-config`} target='_blank' rel='noreferrer'>Manage Status List</a>
                                    </div>
                                </FormGroup>
                            </Col>
                        )}
                    </Row>

                    <p style={sectionHelperStyle}>Optional notes visible to your team.</p>

                    <FormGroup style={{ marginBottom: 8 }}>
                        <ControlLabel htmlFor='reviewComments' style={fieldLabelStyle}>COMMENTS (optional)</ControlLabel>
                        <FormControl
                            componentClass='textarea'
                            style={{ height: 80, resize: 'vertical', marginBottom: 4, ...fieldSurfaceStyle }}
                            id='reviewComments'
                            name='reviewComments'
                            value={localComments}
                            disabled={props.isReadOnly}
                            onChange={handleFieldChange}
                        />
                        <div style={{ textAlign: 'right', fontSize: 11, color: '#888' }}>{commentsLen}/{reviewCommentsMax}</div>
                    </FormGroup>
                </div>
                <div style={cardFooterStyle}>
                    <Button onClick={discardCenter} disabled={props.isReadOnly}>Discard</Button>
                    <Button bsStyle='primary' style={primaryBtnStyle} onClick={() => void saveCenter()} disabled={props.isReadOnly || !isCenterCardValid || !isCenterDirty}>Save</Button>
                </div>
            </div>

            <div style={cardStyle}>
                <div style={cardHeaderStyle}>
                    <div style={cardTitleRowStyle}>
                        {/* <span style={cardNumStyle}>2</span> */}
                        <span style={cardTitleTextStyle}>IN THE NKR PROGRAM (NETWORK STATUS)</span>
                    </div>
                </div>
                <div style={cardBodyStyle}>
                    <p style={{ ...sectionHelperStyle, marginBottom: 12 }}>
                        Shared milestones when the donor is linked to a recipient at another center, or whenever you need to report workup, pre-op, or donation dates.
                    </p>

                    {isNetworkStatusRequired && props.data.recipientCenterIsNkrParticipant === false && (
                        <div style={dangerBannerStyle}>
                            Recipient care is at {props.data.recipientCenterAlias || 'another center'} <strong>This is not an NKR Member Center.</strong> Set network status so coordinating centers stay aligned.
                        </div>
                    )}

                    {isNetworkStatusRequired && props.data.recipientCenterIsNkrParticipant !== false && (
                        <div style={bannerStyle}>
                            <strong>Recipient care is at {props.data.recipientCenterAlias || 'another center'}.</strong> Set network status so coordinating centers stay aligned.
                        </div>
                    )}

                    {props.data.isInvitedToAdvancedReimbursement && (
                        <div className='alert alert-info' role='alert' style={{ marginBottom: 12 }}>
                            {[
                                `Invited to Donor Shield Advanced Reimbursement${props.data.donorShieldInvitationSentDate ? ` on ${props.data.donorShieldInvitationSentDate}` : ''}`,
                                isWorkupScheduled && props.data.remoteStatusDate ? `Workup scheduled: ${props.data.remoteStatusDate}` : null,
                                isPreOpScheduled && props.data.remoteStatusDate ? `Pre-op scheduled: ${props.data.remoteStatusDate}` : null,
                            ].filter(Boolean).join('. ') + '.'}
                        </div>
                    )}

                    {props.data.donorShieldInvitePendingWorkup && !props.data.isInvitedToDonorShield && (
                        <div className='alert alert-warning' role='alert' style={{ marginBottom: 12 }}>
                            {isPreOpScheduled
                                ? 'Please schedule pre-op with a future date to initiate the Donor Shield Advanced Reimbursement process.'
                                : isWorkupScheduled
                                    ? 'Please schedule workup with a future date to initiate the Donor Shield Advanced Reimbursement process.'
                                    : 'Please schedule workup or pre-op with a future date to initiate the Donor Shield Advanced Reimbursement process.'}
                        </div>
                    )}

                    <FormGroup validationState={isNetworkStatusValid ? null : 'error'}>
                        <ControlLabel htmlFor='remoteStatus' style={fieldLabelStyle}>NETWORK STATUS *</ControlLabel>
                        <FormControl
                            id='remoteStatus'
                            name='remoteStatus'
                            componentClass='select'
                            className='form-control'
                            style={fieldSurfaceStyle}
                            value={props.data.remoteStatus ?? -1}
                            disabled={props.isReadOnly}
                            onChange={handleFieldChange}
                        >
                            <option value='-1'>— Select status —</option>
                            {props.remoteStatusList.map((item, index) =>
                                <option key={`remoteStatus-${index}`} value={item.id}>{item.description}</option>
                            )}
                        </FormControl>
                    </FormGroup>

                    {showOnHoldStatusField && (
                        <FormGroup validationState={!preventNetworkSave ? null : 'error'}>
                            <ControlLabel htmlFor='onHoldStatusReason' style={fieldLabelStyle}>ON HOLD REASON</ControlLabel>
                            <FormControl
                                id='onHoldStatusReason'
                                type='text'
                                name='onHoldStatusReason'
                                className='form-control'
                                style={fieldSurfaceStyle}
                                value={onHoldStatusReason?.collectedData ?? ''}
                                disabled={props.isReadOnly}
                                onChange={handleFieldChange}
                            />
                        </FormGroup>
                    )}

                    {selectedRemoteStatus?.requiresDate && (
                        <FormGroup validationState={isNetworkStatusDateValid && !showWorkupFutureDateError && !showPreOpFutureDateError ? null : 'error'}>
                            <ControlLabel style={fieldLabelStyle}>{selectedRemoteStatus.description} date</ControlLabel>
                            <FormControl
                                type='date'
                                id='remoteStatusDate'
                                name='remoteStatusDate'
                                style={fieldSurfaceStyle}
                                value={getDateInputValue('remoteStatusDate', props.data.remoteStatusDate)}
                                disabled={props.isReadOnly}
                                onFocus={() => handleDateFocus('remoteStatusDate', props.data.remoteStatusDate)}
                                onChange={handleDateChange}
                                onBlur={() => handleDateBlur('remoteStatusDate')}
                            />
                            {showWorkupFutureDateError && (
                                <div className='help-block'>Workup scheduled date must be in the future.</div>
                            )}
                            {showPreOpFutureDateError && (
                                <div className='help-block'>Pre-op scheduled date must be in the future.</div>
                            )}
                        </FormGroup>
                    )}
                </div>
                <div style={cardFooterStyle}>
                    <Button onClick={discardNetwork} disabled={props.isReadOnly}>Discard</Button>
                    <Button bsStyle='primary' style={primaryBtnStyle} onClick={() => void saveNetwork()} disabled={props.isReadOnly || !isNetworkCardValid}>Save</Button>
                </div>
            </div>

            {showRuleOutModal && props.ruleOutReasons && props.onRuleOut && (
                <RuleOutDonorModal
                    show={showRuleOutModal}
                    donorName={props.data.fullName || `${props.data.firstName} ${props.data.lastName}`}
                    onHide={handleRuleOutCancel}
                    onRuleOut={handleRuleOutConfirm}
                    preSelectedReasonId={preSelectedReasonId}
                    ruleOutReasons={props.ruleOutReasons}
                />
            )}
        </>
    );
};

export default DashStatusForm;
